import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { Cpu, Network, ArrowRight } from "lucide-react";

const TOOLS = [
  {
    id: "riser-diagrams",
    name: "Interactive Riser Diagrams",
    desc: "Click any device to see its NFPA citation, voltage drop notes, and the lesson it ties to. 4 building types: small commercial, high-rise w/ EVACS, healthcare defend-in-place, and mixed-use residential.",
    href: "/lab/risers",
    icon: Network,
    cta: "Open risers",
  },
  {
    id: "panel-simulator",
    name: "FACU Panel Simulator",
    desc: "Practice acknowledging alarms, silencing notification, resetting the system, and running drills on a working simulated fire alarm control unit. 10 guided scenarios + free-play mode.",
    href: "/lab/panel",
    icon: Cpu,
    cta: "Launch simulator",
  },
];

export default function Lab() {
  useDocumentMeta({
    title: "Interactive Lab — Riser diagrams + FACU simulator",
    description: "Hands-on practice tools for NICET prep: clickable riser diagrams across 4 building types, plus a working fire alarm control panel simulator with 10 guided scenarios.",
    path: "/lab",
  });
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-14" data-testid="lab-page">
        <div className="overline mb-3"><span className="red-bar" />Interactive Lab</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
          Practice on real systems.<br />
          <span className="text-red-600">Without leaving the chair.</span>
        </h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-10">
          The lab gives you hands-on experience with the same systems you'll see on the NICET work elements
          and the FDNY S-98 exam. Click through riser diagrams to inspect every device, or fire up the panel
          simulator to drill alarm response sequences.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          {TOOLS.map((t) => (
            <Link
              key={t.id}
              to={t.href}
              className="card-tech p-7 hover:border-red-600 transition-colors group"
              data-testid={`lab-card-${t.id}`}
            >
              <div className="w-10 h-10 bg-red-50 text-red-600 grid place-items-center mb-4 group-hover:bg-red-600 group-hover:text-white transition-colors">
                <t.icon className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">{t.name}</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-4">{t.desc}</p>
              <div className="inline-flex items-center gap-1.5 text-sm text-red-600 font-semibold">
                {t.cta} <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-sm text-zinc-600">
          <Link to="/dashboard" className="hover:text-red-600" data-testid="lab-back-link">
            ← Back to dashboard
          </Link>
        </div>
      </main>
    </div>
  );
}
